import _ from 'lodash';
import {
  computed,
  makeObservable,
  observable,
  observe,
  toJS,
  when,
} from 'mobx';
import { Observer, observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  Col,
  DatePicker,
  Modal,
  Popover,
  Row,
  Select,
  Tag,
  Tooltip,
} from 'antd';

import Timezones from 'components/Timezones.json';
import { PitstopButton, PitstopSelect, PitstopTable } from 'shared';

import {
  AppStore,
  CurrentUserStore,
  ShopStore,
  TableStore,
  UserStore,
} from 'stores';
import { RolesOptions } from 'stores/Classes/UserObject';

import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Search from 'antd/lib/input/Search';
import { Logger } from 'stores/Classes';
import SaveUserCarsModal from './SaveUserCarsModal';
import SelectCars from './SelectCars';
import ViewUserCars from './ViewUserCars';
import UnsibscribeAlertsModal from './modals/UnsubscribeAlertsModal';

const ButtonsContainer = styled.div`
  margin: 15px 0px;

  #setupBtn {
    margin-right: 20px;
  }
`;

class UsersTable extends Component {
  static propTypes = {
    shopId: PropTypes.number,
    isUserSelectionEnabled: PropTypes.bool,
    enableUserSelection: PropTypes.func,
    isDateSelectionEnabled: PropTypes.bool,
    isPrioritySelectionEnabled: PropTypes.bool,
    isVehicleSelectionEnabled: PropTypes.bool,
    sendEmailConfiguration: PropTypes.func,
    sendPriorityAlertsEmailConfiguration: PropTypes.func,
    sendFleetSegmentConfiguration: PropTypes.func,
    getCurrentSelectedPriorities: PropTypes.func,
    columnKeys: PropTypes.array,
    isEditable: PropTypes.bool,
    showSetupButton: PropTypes.bool,
    onChangeUserIdsSelected: PropTypes.func,
    onClickNotification: PropTypes.func,
    ref: PropTypes.object,
  };

  SAVE_USER_CAR_MODAL = 'SAVE_USER_CAR_MODAL';
  VEHICLE_HEALTH_REPORT_MODAL = 'VEHICLE_HEALTH_REPORT_MODAL';
  EDIT_VEHICLE_HEALTH_REPORT_MODAL_ID = 'EDIT_VEHICLE_HEALTH_REPORT_MODAL_ID';
  ADD_VEHICLE_HEALTH_REPORT_MODAL_ID = 'ADD_VEHICLE_HEALTH_REPORT_MODAL_ID';
  EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID =
    'EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID';
  UNSUBSCRIBE_ALERTS_MODAL_ID = 'UNSUBSCRIBE_ALERTS_MODAL_ID';

  EDIT_ISSUE_ALLERT_MODAL_ID = 'EDIT_ISSUE_ALLERT_MODAL_ID';
  ADD_ISSUE_ALLERT_MODAL_ID = 'ADD_ISSUE_ALLERT_MODAL_ID';
  CHANGE_USER_NOTIFICATIONS_MODAL_ID = 'CHANGE_USER_NOTIFICATIONS_MODAL_ID';

  ADD_PM_NOTIFICATIONS_MODAL_ID = 'ADD_PM_NOTIFICATIONS_MODAL_ID';

  state = {
    firstNameSearch: '',
    lastNameSearch: '',
    emailSearch: '',
    selectedUser: undefined,
    selectedUserIds: [],
    emailSendingDate: '',
    timezone: 'America/New_York',
    selectedCarIds: [],
    selectedEmails: [],
    expandedRowKeys: [],
    loading: true,
  };

  debouncedFirstNameSearch = null;
  debouncedLastNameSearch = null;
  debouncedEmailSearch = null;
  debouncedRoleSearch = null;
  componentDidUpdate(prevProps, prevState) {
    if (prevState.firstNameSearch !== this.state.firstNameSearch) {
      clearTimeout(this.debouncedFirstNameSearch);
      this.debouncedFirstNameSearch = setTimeout(() => {
        this.reloadTable();
      }, 500);
    }
    if (prevState.lastNameSearch !== this.state.lastNameSearch) {
      clearTimeout(this.debouncedLastNameSearch);
      this.debouncedLastNameSearch = setTimeout(() => {
        this.reloadTable();
      }, 500);
    }
    if (prevState.emailSearch !== this.state.emailSearch) {
      clearTimeout(this.debouncedEmailSearch);
      this.debouncedEmailSearch = setTimeout(() => {
        this.reloadTable();
      }, 500);
    }
    if (prevState.roleSearch !== this.state.roleSearch) {
      clearTimeout(this.debouncedRoleSearch);
      this.debouncedRoleSearch = setTimeout(() => {
        this.reloadTable();
      }, 500);
    }
    if (!_.isEqual(this.props.selectedUserIds, prevProps.selectedUserIds)) {
      this.setState({ selectedUserIds: this.props.selectedUserIds || [] });
    }
  }

  get shopId() {
    return ShopStore.currentShop.id;
  }

  disposer = observe(ShopStore.currentShop, 'id', () => {
    this.setState(
      {
        selectedUserIds: [],
      },
      async () => {
        await this.getEmailVehicleHealthReportConfig();
        if (this.tableStore) {
          this.reloadTable();
        }
      }
    );
  });

  componentDidMount() {
    if (this.props.ref) {
      this.props.ref(this);
    }

    // reload table
    this.reloadTable();

    setTimeout(() => {
      this.setState({ loading: false });
    }, 750);
  }

  isIssueModal() {
    return AppStore.openModals.get(this.ADD_ISSUE_ALLERT_MODAL_ID);
  }

  isWeeklyReportModal() {
    return AppStore.openModals.get(this.ADD_VEHICLE_HEALTH_REPORT_MODAL_ID);
  }

  isUserNotificationsModal() {
    return AppStore.openModals.get(this.CHANGE_USER_NOTIFICATIONS_MODAL_ID);
  }

  isPmModel() {
    return AppStore.openModals.get(this.ADD_PM_NOTIFICATIONS_MODAL_ID);
  }

  dataDisposer;
  setTableStore(tableStore) {
    this.unsubscribe();
    this.tableStore = this.props.tableStore || tableStore;
    when(
      () => {
        return (
          !_.isEmpty(tableStore?.data) &&
          (this.isIssueModal() ||
            this.isWeeklyReportModal() ||
            this.isPmModel()) &&
          ShopStore.shopNotifications
        );
      },
      async () => {
        const userIds = toJS(tableStore.data);
        const users = _.map(userIds, (userId) => UserStore.data.get(userId));
        await this.getEmailVehicleHealthReportConfig();
        const vehicleHealthReportObserver = toJS(
          ShopStore.shopNotifications.get(ShopStore.currentShop.id)
        );
        const objects = toJS(
          vehicleHealthReportObserver?.data?.emailConfiguration
        );
        let emailConfigs;
        if (this.isIssueModal()) {
          emailConfigs = _.filter(
            objects,
            (a) => a.email_report_type === 'PriorityAlerts'
          );
        } else if (this.isWeeklyReportModal()) {
          emailConfigs = _.filter(
            objects,
            (a) => a.email_report_type === 'DiagnosticEmail'
          );
        } else if (this.isPmModel()) {
          emailConfigs = _.filter(
            objects,
            (a) => a.email_report_type === 'PmAlerts'
          );
        }
        if (_.isEmpty(users)) return;
        if (_.isEmpty(emailConfigs)) return;

        if (this.isPmModel()) {
          let selectedUserIds = [];
          users.forEach((user) => {
            const userPmAlertsConfigs = _.filter(
              emailConfigs,
              (config) => config.id_user === user.id
            );
            userPmAlertsConfigs.forEach((config) => {
              if (
                config.configuration &&
                !_.isEmpty(config.configuration.actions) &&
                !_.isEmpty(config.configuration.notificationType)
              ) {
                selectedUserIds.push(user.id);
              }
            });
          });
          this.onSelectUsers(_.uniq(selectedUserIds));
        } else {
          const emails = _.map(emailConfigs, (a) => a.emails);
          let selectedUserIds = [];
          for (const emailData of emails) {
            if (!emailData) continue;
            const selectedUser = _.find(
              users,
              (user) => user.email === emailData.email
            );
            if (!_.isNil(selectedUser)) {
              selectedUserIds.push(selectedUser.id);
            }
          }
          // no emails. saved id_user
          const idUsers = _.map(emailConfigs, (a) => a.id_user);
          for (const idUser of idUsers) {
            if (!idUser) continue;
            const selectedUser = _.find(users, (user) => user.id === idUser);
            if (!_.isNil(selectedUser)) {
              selectedUserIds.push(selectedUser.id);
            }
          }
          this.onSelectUsers(_.uniq(selectedUserIds));
        }
      }
    );
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  reloadTable = () => {
    this.tableStore.data = [];
    this.tableStore.reset();
    this.tableStore.reload();
  };

  unsubscribe() {
    if (_.isFunction(this.dataDisposer)) this.dataDisposer();
  }

  get data() {
    let data = _.map(this.tableStore?.data, (id) => UserStore?.data.get(id));

    return _.filter(data, Boolean);
  }

  getEmailVehicleHealthReportConfig = async () => {
    if (
      this.shopId !== -1 &&
      ShopStore.shopNotifications.get(this.shopId) &&
      !ShopStore.shopNotifications.get(this.shopId).pending
    ) {
      await ShopStore.getShopNotifications(this.shopId);
    }
  };

  getCurrentlySelectedPriorities = async () => {
    if (ShopStore.currentShop.id !== -1) {
      let selected = await this.props.getCurrentSelectedPriorities(
        this.props.shopId
      );

      if (selected) {
        this.setState({
          selectedPriorities: selected,
        });
      }
    }
  };

  getUserReports = (user, emailReports) => {
    return _.filter(emailReports, (report) => {
      let emails =
        typeof report.emails === 'string'
          ? JSON.parse(report.emails)
          : report.emails;
      if (!emails && report.id_user && report.id_user === user.id) {
        return true;
      }
      if (!emails) return false;
      return _.find(emails, (e) => e.email === user.email);
    });
  };

  allColumns = [
    {
      key: 'firstName',
      dataIndex: 'firstName',
      editable: true,
      filter: false,
      title: 'First Name',
      required: true,
      width: '150px',
    },
    {
      key: 'lastName',
      dataIndex: 'lastName',
      editable: true,
      filter: false,
      title: 'Last Name',
      required: true,
      width: '150px',
    },
    {
      key: 'email',
      dataIndex: 'email',
      filter: false,
      title: 'Email/Username',
      required: true,
      width: '250px',
      render: (fieldValue, row) => {
        let emailOrUsername = fieldValue;
        if (fieldValue.indexOf('username.pitstop') > -1) {
          emailOrUsername = fieldValue.split('@')[0];
        }
        return <Link to={`/user/${row.userId}`}>{emailOrUsername}</Link>;
      },
    },
    {
      key: 'displayedRole',
      dataIndex: 'displayedRole',
      editable: false,
      filter: false,
      inputType: 'select',
      selectOptions: RolesOptions,
      title: 'Role',
      width: '150px',
      required: true,
    },
    {
      key: 'phone',
      dataIndex: 'phone',
      filter: false,
      editable: true,
      title: 'Phone',
      width: '150px',
      required: false,
    },
    {
      key: 'pm_method',
      dataIndex: 'pm_method',
      filter: false,
      editable: true,
      title: 'Method',
      width: '150px',
      required: false,
      render: (text, user) => {
        return (
          <Observer key={user.id}>
            {() => {
              let vehicleHealthReportObserver = toJS(
                ShopStore.shopNotifications.get(this.shopId)
              );

              const objects = toJS(
                vehicleHealthReportObserver?.data?.emailConfiguration
              );

              const pmAlerts = _.filter(
                objects,
                (object) =>
                  object.email_report_type === 'PmAlerts' &&
                  object.id_user === user.id
              );

              if (_.isEmpty(pmAlerts)) return 'None';

              const notificationTypes = pmAlerts.flatMap(
                (alert) => alert.configuration?.notificationType || []
              );

              const uniqueNotificationTypes = [
                ...new Set(notificationTypes),
              ].join(', ');

              return <span>{uniqueNotificationTypes || 'None'}</span>;
            }}
          </Observer>
        );
      },
    },
    {
      key: 'pm_type',
      dataIndex: 'pm_type',
      filter: false,
      editable: true,
      title: 'PM Types',
      required: false,
      width: '150px',
      render: (text, user) => {
        return (
          <Observer key={user.id}>
            {() => {
              let vehicleHealthReportObserver = toJS(
                ShopStore.shopNotifications.get(this.shopId)
              );

              const objects = toJS(
                vehicleHealthReportObserver?.data?.emailConfiguration
              );

              const pmAlerts = _.filter(
                objects,
                (object) =>
                  object.email_report_type === 'PmAlerts' &&
                  object.id_user === user.id
              );

              if (_.isEmpty(pmAlerts)) return 'None';

              const actionsDescriptions = pmAlerts.flatMap(
                (alert) =>
                  alert.configuration?.actions.map((actionCode) => {
                    switch (actionCode) {
                      case 'O':
                        return 'Overdue';
                      case 'D':
                        return 'Due Soon';
                      case 'U':
                        return 'Upcoming';
                      default:
                        return actionCode;
                    }
                  }) || []
              );

              const uniqueActionsDescriptions = [
                ...new Set(actionsDescriptions),
              ].join(', ');

              return <span>{uniqueActionsDescriptions || 'None'}</span>;
            }}
          </Observer>
        );
      },
    },
    {
      title: 'Maintenance Report Schedule',
      key: 'vehicleHealthReport',
      filter: false,
      required: true,
      width: '200px',
      render: (user) => {
        return (
          <Observer key={'1'}>
            {() => {
              let vehicleHealthReportObserver = toJS(
                ShopStore.shopNotifications.get(ShopStore.currentShop.id)
              );
              const objects = toJS(
                vehicleHealthReportObserver?.data?.get('emailConfiguration')
              );

              const diagnosticNotifications = _.filter(
                objects,
                (object) => object.email_report_type === 'DiagnosticEmail'
              );

              if (_.isEmpty(diagnosticNotifications)) return 'None';

              // current user notifications
              const userReports = this.getUserReports(
                user,
                diagnosticNotifications
              );

              if (_.isEmpty(userReports)) return 'None';

              let result = _.map(userReports, (report) => {
                if (!_.isNil(report?.send_next_email_at)) {
                  return (
                    <div key={user.email} style={{ display: 'flex' }}>
                      <p style={{ fontSize: 14, margin: 'auto' }}>
                        {moment(report.send_next_email_at).format('LLL')}
                      </p>
                    </div>
                  );
                }
              });

              return _.isEmpty(_.filter(result, Boolean))
                ? 'None'
                : _.map(result, (item) => item);
            }}
          </Observer>
        );
      },
    },
    {
      title: 'Vehicle Priority Alerts',
      key: 'vehiclePriorityAlerts',
      width: '200px',
      filter: false,
      required: true,
      render: (user) => {
        return (
          <Observer key={user.id}>
            {() => {
              let vehicleHealthReportObserver = toJS(
                ShopStore.shopNotifications.get(this.shopId)
              );

              const objects = toJS(
                vehicleHealthReportObserver?.data?.get('emailConfiguration')
              );

              const priorityAlerts = _.filter(
                objects,
                (object) => object.email_report_type === 'PriorityAlerts'
              );

              if (_.isEmpty(priorityAlerts)) return 'None';

              // current user reports
              const userReports = this.getUserReports(user, priorityAlerts);

              if (_.isEmpty(userReports)) return 'None';

              // Track which priority types we've already added
              const addedPriorities = new Set();

              let result = _.map(userReports, (report) => {
                const priorities = report?.configuration?.subscribeTo;
                if (!_.isEmpty(priorities)) {
                  // Check for each priority type
                  const hasMinor = _.some(
                    priorities,
                    (p) => p === 0 || p === 1
                  );
                  const hasMajor = _.some(
                    priorities,
                    (p) => p === 2 || p === 3
                  );
                  const hasCritical = _.some(
                    priorities,
                    (p) => p === 4 || p === 5
                  );

                  const toReturn = (
                    <Row
                      key={`${user.id}-${_.random(1000)}`}
                      style={{ width: '168px' }}
                    >
                      {hasMinor && !addedPriorities.has('minor') && (
                        <Col span={8}>
                          <Tag color="#dec431">Minor</Tag>
                        </Col>
                      )}

                      {hasMajor && !addedPriorities.has('major') && (
                        <Col span={8}>
                          <Tag color="#e28931">Major</Tag>
                        </Col>
                      )}

                      {hasCritical && !addedPriorities.has('critical') && (
                        <Col span={8}>
                          <Tag color="#f92d2e">Critical</Tag>
                        </Col>
                      )}
                    </Row>
                  );

                  if (hasMinor) {
                    addedPriorities.add('minor');
                  }

                  if (hasMajor) {
                    addedPriorities.add('major');
                  }

                  if (hasCritical) {
                    addedPriorities.add('critical');
                  }

                  return toReturn;
                }
              });

              return _.isEmpty(_.filter(result, Boolean))
                ? 'None'
                : _.map(result, (item) => item);
            }}
          </Observer>
        );
      },
    },
    {
      title: 'Work Order Notifications',
      key: 'workOrderNotifications',
      width: '200px',
      filter: false,
      required: true,
      render: (user) => {
        return (
          <Observer key={`${user.id}-workOrderNotification-${uuidv4()}`}>
            {() => {
              const vehicleHealthReportObserver = toJS(
                ShopStore.shopNotifications.get(this.shopId)
              );

              const configRecords = toJS(
                vehicleHealthReportObserver?.data?.get('workOrderConfiguration')
              );

              const configByUserId = _.find(
                configRecords,
                (i) => i.userId === user.id
              );

              const byEmail = _.get(configByUserId, 'byEmail');
              const bySMS = _.get(configByUserId, 'bySMS');

              const methods = [];
              if (bySMS) {
                methods.push('Text');
              }
              if (byEmail) {
                methods.push('Email');
              }
              return methods.join(', ') || 'None';
            }}
          </Observer>
        );
      },
    },
    {
      title: 'Associated Shops',
      key: 'associatedShops',
      width: '200px',
      filter: false,
      required: true,
      render: (user) => {
        if (!user.associatedShops || user.associatedShops.length === 0)
          return 'None';

        // Find if current shop is in the associated shops
        const currentShopId = ShopStore.currentShop?.id;
        const currentShopIndex = user.associatedShops.findIndex(
          (s) => s.shopId === currentShopId
        );

        // Prioritize current shop or use the first shop
        const primaryShopIndex = currentShopIndex >= 0 ? currentShopIndex : 0;
        const primaryShop = user.associatedShops[primaryShopIndex];
        const otherShopsCount = user.associatedShops.length - 1;

        // Check if user has access to the primary shop
        const hasAccessToPrimaryShop = Array.from(ShopStore.data.values()).some(
          (shop) => shop.id === primaryShop.shopId
        );

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <div>
              {hasAccessToPrimaryShop ? (
                primaryShop.shopName
              ) : (
                <Tooltip title="You don't have access to this shop">
                  <span style={{ filter: 'blur(3px)' }}>
                    {primaryShop.shopName}
                  </span>
                </Tooltip>
              )}
            </div>
            {otherShopsCount > 0 && (
              <Popover
                title="Associated Shops"
                trigger="click"
                content={
                  <div style={{ maxWidth: '300px' }}>
                    <table
                      style={{ width: '100%', borderCollapse: 'collapse' }}
                    >
                      <tbody>
                        {user.associatedShops.map((s) => {
                          // Check if user has access to this shop
                          const hasAccessToShop = Array.from(
                            ShopStore.data.values()
                          ).some((shop) => shop.id === s.shopId);

                          return (
                            <tr
                              key={s.shopId}
                              style={{ borderBottom: '1px solid #f0f0f0' }}
                            >
                              <td
                                style={{
                                  padding: '8px 4px',
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {hasAccessToShop ? (
                                  <Tooltip title={s.shopName}>
                                    {s.shopName}
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="You don't have access to this shop">
                                    <span style={{ filter: 'blur(3px)' }}>
                                      {s.shopName}
                                    </span>
                                  </Tooltip>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: '8px 4px',
                                  textAlign: 'right',
                                }}
                              >
                                {s.shopId !== ShopStore.currentShop?.id && (
                                  <Tooltip
                                    title={
                                      !hasAccessToShop
                                        ? 'You don\'t have access to this shop'
                                        : ''
                                    }
                                  >
                                    <span>
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        disabled={!hasAccessToShop}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          Modal.confirm({
                                            title: 'Remove Shop Association',
                                            content: `Are you sure you want to remove ${user.firstName} ${user.lastName} from ${s.shopName}?`,
                                            onOk: async () => {
                                              await UserStore.removeUserFromShop(
                                                user.id,
                                                s.shopId
                                              );
                                              // Refresh the user list
                                              this.tableStore.reset();
                                              this.tableStore.reload();
                                            },
                                          });
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                }
              >
                <span
                  style={{
                    background: '#f0f0f0',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                >
                  +{otherShopsCount} more
                </span>
              </Popover>
            )}
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ marginTop: '4px', padding: '0' }}
              onClick={(e) => {
                e.stopPropagation();

                // Get shops the current user has access to
                const accessibleShops = Array.from(ShopStore.data.values());

                // Filter out shops the user is already associated with
                const availableShops = accessibleShops.filter(
                  (shop) =>
                    !user.associatedShops.some((s) => s.shopId === shop.id)
                );

                if (availableShops.length === 0) {
                  AppStore.addNotification(
                    'User is already associated with all shops you have access to.'
                  );
                  return;
                }

                let selectedShopId = null;

                Modal.confirm({
                  title: 'Associate User with Shop',
                  content: (
                    <div>
                      <p>
                        Select the shops to associate with {user.firstName}{' '}
                        {user.lastName}:
                      </p>
                      <PitstopSelect
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select a shop"
                        id="shop-select"
                        selectOptionDisplayField="label"
                        selectOptionValueField="value"
                        options={availableShops.map((shop) => ({
                          label: shop.name,
                          value: shop.id,
                        }))}
                        onChange={(value) => {
                          selectedShopId = value;
                        }}
                      />
                    </div>
                  ),
                  onOk: async () => {
                    const shopIds = selectedShopId;
                    if (!shopIds || shopIds.length === 0) {
                      AppStore.addError('Please select at least one shop');
                      return Promise.reject('No shop selected');
                    }

                    try {
                      await Promise.all(
                        shopIds.map((shopId) => {
                          return UserStore.addUserToShop({
                            email: user.email,
                            username: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            shopRole: user.role,
                            shopId: shopId,
                            userId: user.id,
                            inviterId: CurrentUserStore.user.id,
                          });
                        })
                      );
                      AppStore.addSuccess(
                        'User successfully associated with shops'
                      );
                      // Refresh the user list
                      this.tableStore.reset();
                      this.tableStore.reload();
                      return Promise.resolve();
                    } catch (error) {
                      AppStore.addError('Failed to associate user with shops');
                      return Promise.reject(error);
                    }
                  },
                });
              }}
            >
              Associate with shop
            </Button>
          </div>
        );
      },
    },
  ];

  actionColumns = () => [
    {
      key: 'edit',
      onClick: () => {},
      component: () => {
        return <div>Edit User</div>;
      },
    },
    {
      key: 'delete',
      onClick: () => {},
      component: () => {
        return <div>Delete User</div>;
      },
    },
  ];

  usersNotifications = (user) => {
    const result = {};
    let vehicleHealthReportObserver = toJS(
      ShopStore.shopNotifications.get(this.shopId)
    );

    const objects = toJS(
      vehicleHealthReportObserver?.data?.get('emailConfiguration')
    );

    const priorityAlerts = _.filter(
      objects,
      (object) => object.email_report_type === 'PriorityAlerts'
    );

    const diagnosticNotifications = _.filter(
      objects,
      (object) => object.email_report_type === 'DiagnosticEmail'
    );

    if (_.isEmpty(priorityAlerts)) {
      result.priorities = {};
    }

    if (_.isEmpty(diagnosticNotifications)) {
      result.diagnostics = {};
    }

    const userPriorityReports = this.getUserReports(user, priorityAlerts);

    if (_.isEmpty(userPriorityReports)) {
      result.priorities = {};
    }

    const userDiagnosticsReports = this.getUserReports(
      user,
      diagnosticNotifications
    );

    if (_.isEmpty(userDiagnosticsReports)) {
      result.diagnostics = {};
    }

    let diagnostic = _.map(userDiagnosticsReports, (report) => {
      if (!_.isNil(report?.send_next_email_at)) {
        return moment(report.send_next_email_at).format('LLL');
      }
    });

    if (!_.isEmpty(_.filter(diagnostic, Boolean))) {
      result.diagnostics = _.map(diagnostic, (item) => item);
    }

    const addedPriorities = new Set();

    _.forEach(userPriorityReports, (report) => {
      const priorities = report?.configuration?.subscribeTo;
      if (!_.isEmpty(priorities)) {
        if (_.some(priorities, (p) => p === 0 || p === 1)) {
          addedPriorities.add('minor');
        }
        if (_.some(priorities, (p) => p === 2 || p === 3)) {
          addedPriorities.add('major');
        }
        if (_.some(priorities, (p) => p === 4 || p === 5)) {
          addedPriorities.add('critical');
        }
      }
    });

    result.priorities = Array.from(addedPriorities);
    result.userId = user.id;
    this.setState({ usersNotifications: result });
    AppStore.openModals.set(this.UNSUBSCRIBE_ALERTS_MODAL_ID, true);
    return null;
  };

  transformColumnsToNotEditable = (rows, propIsEditable) => {
    const isEditable = _.isNil(propIsEditable) ? true : propIsEditable;
    if (isEditable === false) {
      return _.map(rows, (row) => {
        return { ...row, editable: false };
      });
    } else {
      return rows;
    }
  };

  generateColumns = (propsColumnKeys) => {
    const isPMNotificationsModalOpen = AppStore.openModals.get(
      this.ADD_PM_NOTIFICATIONS_MODAL_ID
    );
    let columns = this.allColumns.map((column) => ({
      ...column,
      filter: isPMNotificationsModalOpen ? false : column.filter,
    }));

    //Is there a reason why some columns are removed when modals are open?
    // if (AppStore.openModals.get(this.ADD_ISSUE_ALLERT_MODAL_ID)) {
    //   columns = _.filter(
    //     columns,
    //     (col) =>
    //       !_.includes(
    //         [
    //           'displayedRole',
    //           'phone',
    //           'totalCarsAssociated',
    //           'vehicleHealthReport',
    //           'workOrderNotifications',
    //         ],
    //         col.key
    //       )
    //   );
    // }

    // if (AppStore.openModals.get(this.ADD_VEHICLE_HEALTH_REPORT_MODAL_ID)) {
    //   columns = _.filter(
    //     columns,
    //     (col) =>
    //       !_.includes(
    //         [
    //           'displayedRole',
    //           'phone',
    //           'totalCarsAssociated',
    //           'vehiclePriorityAlerts',
    //           'workOrderNotifications',
    //         ],
    //         col.key
    //       )
    //   );
    // }

    if (
      AppStore.openModals.get(this.EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID) &&
      this.props.type === 'WOnotifications_modal'
    ) {
      columns = _.filter(
        columns,
        (col) =>
          !_.includes(
            [
              'displayedRole',
              'totalCarsAssociated',
              'vehicleHealthReport',
              'vehiclePriorityAlerts',
            ],
            col.key
          )
      );
    }

    if (
      isPMNotificationsModalOpen &&
      this.props.type === 'PMnotifications_modal'
    ) {
      columns = _.filter(
        columns,
        (col) =>
          !_.includes(
            [
              'displayedRole',
              'totalCarsAssociated',
              'vehicleHealthReport',
              'vehiclePriorityAlerts',
              'workOrderNotifications',
            ],
            col.key
          )
      );
    }

    if (_.isEmpty(propsColumnKeys)) {
      return this.transformColumnsToNotEditable(columns, this.props.isEditable);
    }

    let filteredColumns = _.filter(columns, (column) => {
      return (
        _.includes(propsColumnKeys, column.key) ||
        _.includes(propsColumnKeys, column.dataIndex) ||
        column.required
      );
    });

    return this.transformColumnsToNotEditable(
      filteredColumns,
      this.props.isEditable
    );
  };

  onEdit = async ({ newValue: updatedUser }) => {
    if (updatedUser) {
      if (!updatedUser.firstName.trim()) {
        AppStore.addError('First name is required');
        return;
      }
      if (!updatedUser.lastName.trim()) {
        AppStore.addError('Last name is required');
        return;
      }
      await UserStore.updateUser(updatedUser.id, updatedUser);
      CurrentUserStore.getCurrentUser();
      this.props.redirectToUserManagement();
    }
    // reload the table
    this.tableStore.reset();
    this.tableStore.reload();

    // this.setState({
    //   selectedUserIds: [updatedUser.id],
    // });
  };

  onDelete = async (user) => {
    try {
      AppStore.addLoading('Removing user...');
      await UserStore.deleteUser(user.id);
    } catch (error) {
      Logger.error(error);
      AppStore.addError('Failed to remove user');
    } finally {
      AppStore.removeLoading('Removing user...');
    }
  };

  onSelectUsers = (userIds) => {
    this.setState({
      selectedUserIds: userIds,
    });
    const userIdsSelected = this.data
      .filter((a) => userIds.includes(a.id))
      .map((a) => {
        return {
          id: a.id,
          name: `${a.firstName} ${a.lastName}`,
          firstName: a.firstName,
          lastName: a.lastName,
          email: a.email,
          phone: a.phone,
        };
      });
    const updateSelectedIds = this.props.onChangeUserIdsSelected;
    if (_.isFunction(updateSelectedIds)) updateSelectedIds(userIdsSelected);
  };

  sendEmailConfiguration = async () => {
    let { emailSendingDate, timezone, selectedUserIds } = this.state;

    await this.props.sendEmailConfiguration(
      emailSendingDate,
      timezone,
      selectedUserIds
    );

    this.setState({
      selectedUserIds: [],
      emailSendingDate: '',
    });
  };

  sendPriorityAlertsEmailConfiguration = async () => {
    let { selectedPriorities } = this.state;

    await this.props.sendPriorityAlertsEmailConfiguration(selectedPriorities);

    this.getCurrentlySelectedPriorities();
  };

  sendFleetSegmentConfiguration = async () => {
    let { selectedCarIds, selectedUserIds } = this.state;

    await this.props.sendFleetSegmentConfiguration(
      selectedCarIds,
      selectedUserIds
    );
  };

  onChange = (e) => {
    let selectedPriorities = this.state.selectedPriorities;
    for (let i = 0; i < e.length; i++) {
      if (selectedPriorities.includes(e[i])) {
        selectedPriorities = selectedPriorities.filter(
          (value) => value !== e[i]
        );
      } else {
        selectedPriorities.push(e[i]);
      }
    }

    this.setState({ selectedPriorities: selectedPriorities });
  };

  onSaveModal = () => {
    // refresh the user list
    this.tableStore.reset();
  };

  // Check if the modal is open
  get isModalOpen() {
    return (
      AppStore.openModals.get(this.EDIT_WORK_ORDER_NOTIFICATION_MODAL_ID) ||
      AppStore.openModals.get(this.ADD_PM_NOTIFICATIONS_MODAL_ID)
    );
  }

  clearFiltersAndCollapse = () => {
    this.setState(
      {
        firstNameSearch: '',
        lastNameSearch: '',
        emailSearch: '',
        roleSearch: undefined,
        expandedRowKeys: [],
      },
      () => {
        if (this.tableStore) {
          this.tableStore.reset();
          this.tableStore.reload();
        }
      }
    );
  };

  render() {
    const { selectedUserIds, selectedCarIds } = this.state;
    const {
      isUserSelectionEnabled,
      enableUserSelection,
      isDateSelectionEnabled,
      isPrioritySelectionEnabled,
      isVehicleSelectionEnabled,
    } = this.props;

    const showSetupButton = _.isNil(this.props.showSetupButton)
      ? true
      : this.props.showSetupButton;

    const onClickUserNotification = (user) => {
      if (this.props.onClickNotification) {
        this.usersNotifications(user);
        const vehicleHealthReportObserver = toJS(
          ShopStore.shopNotifications.get(ShopStore.currentShop.id)
        );
        const notificationConfiguration = toJS(
          vehicleHealthReportObserver?.data?.emailConfiguration
        );
        this.props.onClickNotification(user, notificationConfiguration);
      }
    };

    return (
      <>
        {isUserSelectionEnabled && (
          <div style={{ margin: '20px 0px' }}>
            {isDateSelectionEnabled && (
              <div>
                <p>
                  <b>Select date to send the vehicle health report:</b>
                </p>

                <div style={{ marginBottom: 18 }}>
                  <InfoCircleOutlined
                    style={{ fontSize: 14, marginRight: 8 }}
                  />
                  <i>
                    The email will be sent automatically every week from the
                    date you set!
                  </i>
                </div>

                <DatePicker
                  showTime
                  onChange={(date) => {
                    this.setState({
                      emailSendingDate: moment(date).toISOString(),
                    });
                  }}
                />

                <Select
                  style={{ width: 200, margin: '0px 15px' }}
                  showSearch
                  placeholder="Timezone"
                  defaultValue="America/New_York"
                  onChange={(timezone) =>
                    this.setState({
                      timezone,
                    })
                  }
                >
                  {Object.keys(Timezones).map((key) => (
                    <Select.Option key={key} value={key}>
                      {Timezones[key]}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        )}
        <Row gutter={[8, 8]}>
          <Col md={this.isModalOpen ? 6 : 3} xs={this.isModalOpen ? 24 : 12}>
            <Search
              placeholder="Search by first name"
              value={this.state.firstNameSearch}
              onChange={(event) => {
                this.setState({
                  firstNameSearch: event.target.value,
                });
              }}
            />
          </Col>

          <Col md={this.isModalOpen ? 6 : 3} xs={this.isModalOpen ? 24 : 12}>
            <Search
              placeholder="Search by last name"
              value={this.state.lastNameSearch}
              onChange={(event) => {
                this.setState({
                  lastNameSearch: event.target.value,
                });
              }}
            />
          </Col>

          <Col md={this.isModalOpen ? 6 : 3} xs={this.isModalOpen ? 24 : 12}>
            <Search
              placeholder="Search by email"
              value={this.state.emailSearch}
              onChange={(event) => {
                this.setState({
                  emailSearch: event.target.value,
                });
              }}
            />
          </Col>

          <Col md={this.isModalOpen ? 6 : 3} xs={this.isModalOpen ? 24 : 12}>
            <Select
              placeholder="Search by role"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              value={this.state.roleSearch}
              onChange={(value) => {
                this.setState({
                  roleSearch: value,
                });
              }}
              style={{ width: '100%' }}
              allowClear
            >
              <Select.Option value="fleetManager">Fleet Manager</Select.Option>
              <Select.Option value="driver">Driver</Select.Option>
              <Select.Option value="technician">Technician</Select.Option>
            </Select>
          </Col>

          <Col span={24}>
            {this.state.loading ? null : (
              <PitstopTable
                id={`${this.shopId}-users-list`}
                columns={this.generateColumns(this.props.columnKeys)}
                data={this.data}
                pagination={isVehicleSelectionEnabled ? { pageSize: 4 } : false}
                tableStore={this.tableStore}
                fetchData={(filters, table) => {
                  table.data = [];
                  const nameFilters = {};
                  if (this.state.firstNameSearch) {
                    nameFilters.firstName = this.state.firstNameSearch;
                  }
                  if (this.state.lastNameSearch) {
                    nameFilters.lastName = this.state.lastNameSearch;
                  }

                  UserStore.fetchUsersForTable(
                    {
                      ...filters,
                      filter: {
                        ...filters.filter,
                        ...nameFilters,
                        email: this.state.emailSearch || '',
                        role: this.state.roleSearch || '',
                      },
                    },
                    table
                  );
                }}
                getTableStore={(tableStore) => this.setTableStore(tableStore)}
                // height={isUserSelectionEnabled ? 200 : null}
                rowKey={(record) => record?.userId || record?.id}
                // expandedRowRender={(user) => {
                //   return (
                //     <ViewUserCars shopId={this.shopId} userId={user.userId} />
                //   );
                // }}
                expandIconAsCell={false}
                expandIconColumnIndex={null}
                onDelete={this.onDelete}
                onEdit={this.onEdit}
                rowSelection={
                  isUserSelectionEnabled
                    ? {
                        selectedRowKeys: selectedUserIds,
                        onChange: this.onSelectUsers,
                        hideDefaultSelections: true,
                        columnWidth: 50,
                      }
                    : undefined
                }
                actionColumns={this.actionColumns()}
                hasNotification={
                  ShopStore.currentShop.id && ShopStore.currentShop.id !== -1
                }
                onClickNotification={onClickUserNotification}
                expandedRowKeys={this.state.expandedRowKeys}
                onExpand={(expanded, record) => {
                  const key = record.userId || record.id;
                  this.setState((prev) => ({
                    expandedRowKeys: expanded
                      ? [...prev.expandedRowKeys, key]
                      : prev.expandedRowKeys.filter((k) => k !== key),
                  }));
                }}
                onExpandedRowsChange={(keys) =>
                  this.setState({ expandedRowKeys: keys })
                }
              />
            )}
          </Col>
        </Row>

        {isVehicleSelectionEnabled && (
          <>
            <p style={{ margin: '15px 0px' }}>
              <b>Select vehicles:</b>
            </p>

            <SelectCars
              id={this.shopId}
              rowSelection={{
                selectedCarIds: [...selectedCarIds],
                onChange: (values) => {
                  this.setState({
                    selectedCarIds: values,
                  });
                },
              }}
            />
          </>
        )}

        {isUserSelectionEnabled && showSetupButton && (
          <ButtonsContainer>
            <PitstopButton
              id="setupBtn"
              onClick={async () => {
                isUserSelectionEnabled && isVehicleSelectionEnabled
                  ? await this.sendFleetSegmentConfiguration()
                  : isPrioritySelectionEnabled
                    ? await this.sendPriorityAlertsEmailConfiguration()
                    : await this.sendEmailConfiguration();

                this.setState({
                  selectedCarIds: [],
                  selectedUserIds: [],
                  selectedPriorities: [],
                });
              }}
            >
              Setup Configuration
            </PitstopButton>
            <PitstopButton
              type="secondary"
              id="cancelBtn"
              onClick={() => {
                enableUserSelection();
                if (isPrioritySelectionEnabled)
                  this.getCurrentlySelectedPriorities();
              }}
            >
              Cancel
            </PitstopButton>
          </ButtonsContainer>
        )}

        {AppStore.openModals.get(this.UNSUBSCRIBE_ALERTS_MODAL_ID) && (
          <UnsibscribeAlertsModal
            id={this.UNSUBSCRIBE_ALERTS_MODAL_ID}
            notifications={this.state.usersNotifications}
            onSuccess={() => {
              // Refresh current user data (might be needed for other UI elements)
              // No need to reset/reload the table - MobX Observer components
              // will automatically update when ShopStore.shopNotifications changes
            }}
          />
        )}

        <SaveUserCarsModal
          id={this.SAVE_USER_CAR_MODAL}
          shopId={ShopStore.currentShop.id}
          user={this.state.selectedUser}
          onSaveModal={this.onSaveModal}
        />
      </>
    );
  }
  constructor(props) {
    super(props);
    this.tableStore = props.tableStore || new TableStore();
    makeObservable(this, {
      tableStore: observable,
      data: computed,
      shopId: computed,
    });
  }
}

export default observer(UsersTable);
